import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MarketingShell } from '../components/MarketingShell';
import { useAuth } from '../context/AuthContext';
import { isSupabaseConfigured } from '../lib/supabase';

// Magic-link sign-in screen. Email-only. The link in the email goes to /auth/callback
// which completes the sign-in and routes to /app.
export function SignInScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signInWithMagicLink, hasAccess } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If we already have access (signed in + subscription or admin), skip straight in.
  if (hasAccess) {
    navigate(params.get('next') ?? '/app', { replace: true });
    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await signInWithMagicLink(email);
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setSent(true);
  }

  if (!isSupabaseConfigured()) {
    return (
      <MarketingShell>
        <div className="max-w-md mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Sign-in not configured yet</h1>
          <p className="mt-3 text-ink/70 leading-relaxed">
            Magic-link sign-in needs Supabase to be set up. If you are the site admin, paste your
            Supabase URL and anon key into the Vercel project env vars and redeploy. Until then,
            this screen is intentionally inert.
          </p>
          <Link to="/" className="inline-flex items-center mt-6 text-accent font-medium hover:underline">
            ← Back to the marketing site
          </Link>
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell>
      <div className="max-w-md mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold tracking-tight text-ink leading-tight">
          {sent ? 'Check your email' : 'Sign in to My Dyslexia Tools'}
        </h1>
        <p className="mt-3 text-ink/65 leading-relaxed">
          {sent
            ? `We sent a sign-in link to ${email}. Tap it on this device and you will be signed in.`
            : 'Enter your email and we will send you a sign-in link. No passwords. No accounts to remember.'}
        </p>

        {!sent && (
          <form onSubmit={submit} className="mt-7 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink/55 font-medium">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-4 py-3 text-ink focus:outline-none focus:ring-4 focus:ring-accent/30"
              />
            </label>
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy || !email}
              className="w-full inline-flex items-center justify-center bg-accent text-white px-6 py-3.5 rounded-full font-medium text-lg hover:brightness-105 transition shadow-lg shadow-accent/20 disabled:opacity-60"
            >
              {busy ? 'Sending link...' : 'Send sign-in link'}
            </button>
            <p className="text-xs text-ink/50 mt-2 leading-relaxed">
              By signing in you agree to our{' '}
              <Link to="/terms" className="underline">
                terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline">
                privacy policy
              </Link>
              . New users get 3 free worksheets to try the app, no payment needed.
            </p>
          </form>
        )}

        {sent && (
          <div className="mt-7">
            <button
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
              className="text-sm text-accent hover:underline"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </MarketingShell>
  );
}
